// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded';

// material ui
import {
  alpha,
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

// shared components
import CardHeaderWithIcon from '@/components/shared/surfaces/card/CardHeaderWithIcon';

// components
import { formatNumber } from '@/utils/formatters';

/**
 * Simplified Declaration Card for Water/Heat ESG pages
 * Shows CO2e emissions, declaration, and consumption in a card format
 */
const WaterHeatDeclarationCard = ({
  icon,
  iconColor,
  energySummary,
  title,
  tooltip,
}) => {
  const { t } = useTranslation('energyxplorer-esg');

  return (
    <Box sx={{ height: '100%' }}>
      <Card
        sx={{
          p: 2,
          borderRadius: 2,
          minHeight: 140,
          height: '100%',
        }}
      >
        <CardHeaderWithIcon
          title={title}
          icon={icon}
          iconColor={iconColor}
          iconBgColor={alpha(iconColor, 0.1)}
          sx={{ borderBottom: '1px solid lightgray' }}
          tooltip={tooltip}
        />
        <CardContent sx={{ height: '100%' }}>
          <Stack
            direction="column"
            color="inherit"
            component={Card}
            elevation={0}
            sx={{
              transition: 'box-shadow 0.3s ease',
              height: '100%',
              background: 'transparent',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <ValueDisplay
                label={t('emission')}
                value={formatNumber(energySummary?.CO2eEmissions / 1000)}
                icon={<Co2RoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                unit={energySummary?.BaseUnitCO2e || 'kg CO2e'}
              />

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <ValueDisplay
                label={t('decleration')}
                value={formatNumber(energySummary?.CO2eEmissionsPerUnit * 1000)}
                icon={<WbCloudyRoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                unit={`g/${energySummary?.BaseUnitEnergy || 'kWh'}`}
              />

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <ValueDisplay
                label={t('consumption')}
                value={`${formatNumber(energySummary?.EnergyUsageEligible ?? energySummary?.EnergyUsage)}`}
                icon={<BoltRoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                unit={energySummary?.BaseUnitEnergy || 'kWh'}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const ValueDisplay = ({ label, value, icon, unit }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      <Box sx={{ ml: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1" color="textPrimary" fontWeight="bold">
          {value} {unit}
        </Typography>
      </Box>
    </Box>
  );
};

export default WaterHeatDeclarationCard;
